import { useEffect, useCallback, useRef } from "react";
import { useFirebaseAuth } from "./useFirebaseAuth";
import {
  createSession,
  validateAndCleanupSession,
  updateActivity,
  getCurrentSession,
  getSessionStatus,
} from "../utils/sessionManager";

export const useEnhancedSession = () => {
  const { user } = useFirebaseAuth();
  const activityTimeoutRef = useRef<NodeJS.Timeout>();
  const validationIntervalRef = useRef<NodeJS.Timeout>();

  // Track user activity
  const trackActivity = useCallback(() => {
    updateActivity();

    // Reset inactivity timer
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Set new inactivity timer (2 hours)
    activityTimeoutRef.current = setTimeout(() => {
      validateAndCleanupSession();
    }, 2 * 60 * 60 * 1000);
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttle activity tracking to avoid excessive updates
    let lastActivity = 0;
    const throttledTrackActivity = () => {
      const now = Date.now();
      if (now - lastActivity > 60000) {
        // Only update once per minute
        lastActivity = now;
        trackActivity();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledTrackActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledTrackActivity, true);
      });
    };
  }, [user, trackActivity]);

  // Initialize session when user logs in
  useEffect(() => {
    if (user) {
      const existingSession = getCurrentSession();
      if (!existingSession) {
        createSession();
      }
      trackActivity(); // Start activity tracking
    }
  }, [user, trackActivity]);

  // Periodic session validation
  useEffect(() => {
    if (!user) return;

    // Validate session every 5 minutes
    validationIntervalRef.current = setInterval(async () => {
      const isValid = await validateAndCleanupSession();
      if (!isValid) {
      }
    }, 5 * 60 * 1000);

    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
  }, []);

  // Handle page visibility change
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && user) {
        // When user returns to tab, validate session
        const isValid = await validateAndCleanupSession();
        if (isValid) {
          trackActivity(); // Update activity
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, trackActivity]);

  // Utility functions for components
  const getDebugInfo = () => {
    return getSessionStatus();
  };

  const forceSessionValidation = async () => {
    return await validateAndCleanupSession();
  };

  return {
    trackActivity,
    getDebugInfo,
    forceSessionValidation,
  };
};
